// @ts-nocheck
// Create/edit a custom registration form field in a modal. Used by the
// registration edit page's Form Fields pane (list-first field manager).
//
// Edit mode keeps `fieldId` STABLE — registrant answers are keyed by it, so a
// rename must never regenerate the id. Permanent fields (First/Last Name, the
// auto-injected Email) lock name/type/required; only the placeholder stays
// editable.
import { useEffect, useState } from 'react';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Lock } from 'lucide-react';
import generateFieldId from '@/utils/generateFieldId';

export const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text', icon: '📝' },
  { value: 'select', label: 'Dropdown', icon: '📋' },
  { value: 'textarea', label: 'Text Area', icon: '📄' },
  { value: 'email', label: 'Email', icon: '✉️' },
  { value: 'phone', label: 'Phone', icon: '📱' },
  { value: 'jersey', label: 'Jersey #', icon: '🏃' },
  { value: 'date', label: 'Date', icon: '📅' },
];

const PRIMITIVE_BY_TYPE = { jersey: 'number' };
const TYPE_PRESET_NAMES = { phone: 'Phone Number', jersey: 'Jersey Number', email: 'Email' };

export default function FieldEditorModal({
  open,
  onOpenChange,
  field = null, // null → create mode
  existingFieldIds = [],
  audienceLabel = 'Players',
  onSave,
}) {
  const editing = !!field;
  const locked = !!field?.permanent;

  const [fieldType, setFieldType] = useState('text');
  const [name, setName] = useState('');
  const [helperText, setHelperText] = useState('');
  const [required, setRequired] = useState(false);
  const [options, setOptions] = useState([]);

  // (Re)seed the form whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setFieldType(field?.fieldType || 'text');
    setName(field?.name || '');
    setHelperText(field?.helperText || '');
    setRequired(!!field?.required);
    setOptions(field?.options || []);
  }, [open, field]);

  const onTypeChange = (v) => {
    setFieldType(v);
    // Presets only help when creating from scratch — never rename an existing field.
    if (!editing && TYPE_PRESET_NAMES[v] && !name) setName(TYPE_PRESET_NAMES[v]);
  };

  const onSubmit = () => {
    const base = editing ? field : {};
    onSave({
      ...base,
      name,
      fieldId: editing ? field.fieldId : generateFieldId(name, existingFieldIds),
      helperText,
      required: locked ? field.required : required,
      fieldType: locked ? field.fieldType : fieldType,
      primitiveType: locked
        ? field.primitiveType
        : (PRIMITIVE_BY_TYPE[fieldType] || 'string'),
      permanent: !!field?.permanent,
      ...(field?.pinned ? { pinned: field.pinned } : {}),
      ...(!locked && fieldType === 'select' ? { options } : {}),
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit “${field?.name}”` : 'Add Field'}</DialogTitle>
          <DialogDescription>
            {locked
              ? 'This is a built-in field. Its name and type are fixed, but you can adjust its placeholder text.'
              : editing
                ? 'Changes apply when you save the Form Fields section.'
                : 'The new field is added to the list; it saves when you save the Form Fields section.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {!locked && (
            <div>
              <Label className="mb-2 block text-sm font-medium">Field type</Label>
              <RadioGroup value={fieldType} onValueChange={onTypeChange} className="flex flex-wrap gap-2">
                {FIELD_TYPE_OPTIONS.map((option) => (
                  <div key={option.value} className="flex items-center">
                    <RadioGroupItem id={`fem-${option.value}`} value={option.value} className="peer sr-only" />
                    <Label
                      htmlFor={`fem-${option.value}`}
                      className="flex cursor-pointer items-center gap-1.5 rounded-lg border-2 border-border bg-card px-3 py-2 text-sm font-medium text-muted-foreground transition-all hover:border-input hover:bg-muted/50 peer-data-[state=checked]:border-foreground peer-data-[state=checked]:bg-foreground peer-data-[state=checked]:text-white"
                    >
                      <span>{option.icon}</span>
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="fem-name" className="mb-2 flex items-center gap-1.5 text-sm font-medium">
                Field name {locked && <Lock className="h-3 w-3 text-muted-foreground" />}
              </Label>
              <Input
                id="fem-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Age, Skill Level, Notes"
                disabled={locked}
              />
            </div>
            <div>
              <Label htmlFor="fem-placeholder" className="mb-2 block text-sm font-medium">Placeholder text</Label>
              <Input
                id="fem-placeholder"
                value={helperText}
                onChange={(e) => setHelperText(e.target.value)}
                placeholder="e.g., Enter your age"
              />
            </div>
          </div>

          {!locked && (
            <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
              <Switch id="fem-required" checked={required} onCheckedChange={setRequired} />
              <Label htmlFor="fem-required" className="cursor-pointer text-sm font-medium">
                Required field
                <span className="ml-2 font-normal text-muted-foreground">({audienceLabel} must fill this out)</span>
              </Label>
            </div>
          )}

          {!locked && fieldType === 'select' && (
            <div>
              <Label className="mb-2 block text-sm font-medium">Dropdown options</Label>
              <Autocomplete
                multiple
                options={options}
                value={options}
                onChange={(e, v) => setOptions(v)}
                freeSolo
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip variant="outlined" label={option} key={key} {...tagProps} />;
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Type an option and press Enter" size="small" />
                )}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={!name.trim()}>
            {editing ? 'Save Changes' : 'Add Field'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
